import React from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  isLoadingSelector as areProductsLoadingSelector,
  hasErrorsSelector as doesProductsHaveErrorsSelector,
  findByProductReleaseId,
  productsDataSelector
} from "selectors/productsSelector.js";

import {
  isLoadingSelector as isProductReleaseLoadingSelector,
  hasErrorsSelector as doesProductReleaseHaveErrorsSelector,
  errorSelector as productReleaseErrorSelector
} from "selectors/productReleaseSelector.js";

import { stockModelIdSelector } from "selectors/stockModelIdSelector.js";
import { buyplanIdSelector, buyplanStateSelector } from "selectors/buyplanIdSelector.js";
import { buyplanInfoDataSelector } from "selectors/buyplanInfoSelector.js";

import {
  addBuyplanItem,
  removeBuyplanItem,
  addStockModalItem,
  removeStockModalItem
} from "actions/likeDislikeActions.js";

import LikeDislike from "components/ProductCard/Actions/LikeDislike/LikeDislike.jsx";

import { trackAnalyticsEvent, buyplanTypeLabel } from "apis/analyticsHelpers.js";

import { DYNAMIC } from "constants.js";

import i18next from "i18next";

const LikeDislikeContainer = ({
  productReleaseId,
  buyplanItemId,
  isDisabled = false,
  isModalContext = false
}) => {
  const dispatch = useDispatch();
  const stockModalId = useSelector(stockModelIdSelector);
  const buyplanId = useSelector(buyplanIdSelector);
  const buyplanState = useSelector(buyplanStateSelector);

  const productRelease = useSelector(findByProductReleaseId(productReleaseId));

  const areProductsLoading = useSelector(areProductsLoadingSelector);
  const doesProductsHaveErrors = useSelector(doesProductsHaveErrorsSelector);
  const productsData = useSelector(productsDataSelector);

  const isProductReleaseLoading = useSelector(isProductReleaseLoadingSelector);
  const doesProductReleaseHaveErrors = useSelector(
    doesProductReleaseHaveErrorsSelector
  );
  const productReleaseError = useSelector(productReleaseErrorSelector);

  const stockModalInfo = useSelector(buyplanInfoDataSelector);
  const isStockModalTemplate = stockModalInfo.get("state") === "template";
  const isDynamic = stockModalInfo.get("cnf_type") === DYNAMIC;

  let isLoading, hasErrors, errorMsg;
  if (isModalContext) {
    isLoading = isProductReleaseLoading;
    hasErrors = doesProductReleaseHaveErrors;
    errorMsg = productReleaseError;
  } else {
    isLoading = areProductsLoading;
    hasErrors = doesProductsHaveErrors;
    errorMsg = productsData.get("message");
  }

  const toggleLiked = () => {
    // Tracked right before each dispatch, not up front — the static-CNF
    // confirm() below can cancel a removal with no dispatch at all, and that
    // shouldn't count as a product_card_action.
    const trackContext = {
      record_type: "product_release",
      record_id: productReleaseId,
      // On a CnF/SuperFill page buyplanId is never populated (StockModel
      // isn't a Buyplan) — send the StockModel's own id instead, so the
      // server denormalizes the right name under the right buyplan_type
      // (see AnalyticsEventWorker#resolve_buyplan_name).
      buyplan_id: stockModalId || buyplanId,
      buyplan_type: buyplanTypeLabel(buyplanState, stockModalInfo.get("sm_type")),
      path: window.location.pathname
    };

    if (stockModalId) {
      const stockModalItemId = productRelease.stock_model_item.id;
      if (stockModalItemId) {
        if (!isStockModalTemplate && !isDynamic) {
          // eslint-disable-next-line no-alert
          if (!window.confirm(i18next.t("stockModalItem.remove.removeItemFromStaticCnf"))) {
            return null;
          }
        }
        trackAnalyticsEvent("product_card_action", { ...trackContext, category: "Unlike" });
        dispatch(removeStockModalItem(stockModalId, stockModalItemId));
      } else {
        trackAnalyticsEvent("product_card_action", { ...trackContext, category: "Like" });
        dispatch(addStockModalItem(stockModalId, productReleaseId));
      }
    } else {
      trackAnalyticsEvent("product_card_action", {
        ...trackContext,
        category: buyplanItemId ? "Unlike" : "Like"
      });
      dispatch(
        buyplanItemId
          ? removeBuyplanItem(buyplanId, buyplanItemId)
          : addBuyplanItem(buyplanId, productReleaseId)
      );
    }
  };

  if (isLoading) {
    return null;
  } else if (hasErrors) {
    return <div className="error-msg">{errorMsg}</div>;
  } else {
    return (
      <LikeDislike
        liked={buyplanItemId}
        toggleLiked={toggleLiked}
        isDisabled={isDisabled}
      />
    );
  }
};

export default React.memo(LikeDislikeContainer);
